import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { buildHeadCells } from '../../../../../utils/methods';
import {
  VALIDATION_FIELD_MAPPING,
  HEAD_CELL_SORT_MAPPING,
  GENERIC_LOOKUP_KEYS,
  STATUS,
} from '../../../../../utils/appConstant';
import { HeadCellType, TableContextProvider } from '../../../../../interfaces/common';
import { ListStockistBranchFilters } from '../../../../../interfaces/stockist.interface';
import useSorting from '../../../../../hooks/useSorting';
import usePagination from '../../../../../hooks/usePagination';
import useFetch from '../../../../../hooks/useFetch';
import StockistBranchService from '../../../../../services/stockist.branch.service';
import CommonService from '../../../../../services/common.service';
import { SortOrder } from '../../../../../enums';
import Filters from '../../list/components/Filters';
import TableBox from '../../../../../components/TableBox';
import SideDrawer from '../../../../../components/SideDrawer';
import BranchAddEdit from './BranchAddEdit';
import BranchRow from './BranchRow';
import BranchListHeader from './BranchListHeader';

interface BranchesTabProps {
  stockistId: string;
}

const BranchesTab: React.FC<BranchesTabProps> = ({ stockistId }) => {
  const { t } = useTranslation();

  const [openDrawer, setOpenDrawer] = useState(false);
  const [selectedBranchId, setSelectedBranchId] = useState<number | null>(null);
  const [refresh, setRefresh] = useState(0);

  const [filters, setFilters] = useState<ListStockistBranchFilters>({
    searchText: '',
    status: STATUS.active,
    stockist_id: Number(stockistId),
  });

  const [dropdownSearchData, setDropdownSearchData] = useState<any>({
    status: '',
  });

  const { order, orderBy, setOrder, setOrderBy } = useSorting({
    defaultOrder: SortOrder.asc,
    defaultOrderBy: VALIDATION_FIELD_MAPPING.name,
  });
  const { page, perPage, setPage, setPerPage } = usePagination({
    defaultPage: 1,
  });

  const headCells: HeadCellType[] = buildHeadCells(
    [
      VALIDATION_FIELD_MAPPING.name,
      VALIDATION_FIELD_MAPPING.city,
      VALIDATION_FIELD_MAPPING.state,
      VALIDATION_FIELD_MAPPING.email,
      VALIDATION_FIELD_MAPPING.status,
      VALIDATION_FIELD_MAPPING.updated_at,
      VALIDATION_FIELD_MAPPING.actions,
    ],
    'stockistBranchList'
  );

  const {
    total,
    data: branches,
    loading,
  } = useFetch({
    service: StockistBranchService,
    func: 'list',
    payload: {
      page,
      perPage,
      orderBy: HEAD_CELL_SORT_MAPPING.stockistBranchList?.[orderBy] || orderBy,
      order,
      searchText: filters.searchText.trim(),
      status: filters.status === STATUS.active ? '1' : filters.status === STATUS.inactive ? '0' : '',
      stockist_id: filters.stockist_id,
    },
    dependency: [
      order,
      orderBy,
      page,
      perPage,
      filters.searchText,
      filters.status,
      filters.stockist_id,
      refresh
    ],
  });

  const { data: allStatus } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.status, searchText: '' },
    dependency: [],
  });

  const { data: status } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.status, searchText: dropdownSearchData.status },
    dependency: [dropdownSearchData.status],
  });

  const handleFilterschange = useCallback((newFilters: any) => {
    setPage(1);
    setOrder(SortOrder.asc);
    setOrderBy(VALIDATION_FIELD_MAPPING.name);
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const handleDropdownSearchInputChange = useCallback(
    (event: any, name: string) => {
      setDropdownSearchData((prev: any) => {
        return {
          ...prev,
          [name]: event.target.value,
        };
      });
    },
    [setDropdownSearchData]
  );

  const handleAddBranch = () => {
    setSelectedBranchId(null);
    setOpenDrawer(true);
  };

  const handleEditBranch = (id: any) => {
    setSelectedBranchId(id);
    setOpenDrawer(true);
  };

  const handleSuccess = () => {
    setOpenDrawer(false);
    setSelectedBranchId(null);
    setRefresh(prev => prev + 1);
  };

  const headerSection = <BranchListHeader total={total} onAdd={handleAddBranch} />;

  const values: TableContextProvider = useMemo(() => {
    return {
      headerSection,
      row: BranchRow,
      data: branches || [],
      pagination: true,
      headCells,
      order,
      orderBy,
      page,
      perPage,
      total,
      loading,
      noDataFoundMessage: t('common.table.noRecordsFound'),
      setPage,
      setPerPage,
      setOrder,
      setOrderBy,
      rowProps: {
        onEdit: handleEditBranch
      }
    };
  }, [
    headCells,
    order,
    orderBy,
    page,
    perPage,
    total,
    loading,
    branches,
    setPage,
    setPerPage,
    setOrder,
    setOrderBy,
    headerSection
  ]);

  return (
    <div className="card-body">
      <Filters
        initialFilters={{ searchText: filters.searchText, status: filters.status }}
        searchPlaceholder={t('stockists.stockistBranch.list.pageTexts.filters.searchPlaceholder')}
        onFilterChange={handleFilterschange}
        status={status}
        allStatus={allStatus}
        dropdownSearchData={dropdownSearchData}
        onDropdownSearchChange={handleDropdownSearchInputChange}
      />
      <TableBox tableValues={values} />

      <SideDrawer
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        title={selectedBranchId ? t('stockists.stockistBranch.addEdit.pageTexts.title.editTitle') : t('stockists.stockistBranch.addEdit.pageTexts.title.addTitle')}
        width={700}
      >
        {openDrawer && (
          <BranchAddEdit
            id={selectedBranchId}
            stockistId={Number(stockistId)}
            onClose={() => setOpenDrawer(false)}
            onSuccess={handleSuccess}
          />
        )}
      </SideDrawer>
    </div>
  );
};

export default BranchesTab;
